/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ApiRequestPendingInterface,
  RequestPendingApiResponseInterface,
  RequestPendingInitialProperties,
} from '../../interface/request-pending-interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import penaltyManagementApi from '../../utils/api/penalty-management';

export const fetchRequestPendingAccount = createAsyncThunk<
  RequestPendingApiResponseInterface, // Return type
  ApiRequestPendingInterface
>(
  'request-pending-account/fetchRequestPendingAccount',
  async (data: ApiRequestPendingInterface, { dispatch }) => {
    try {
      const response = await penaltyManagementApi.fetchRequestAccountByStatus(
        removeEmptyKeys({ ...data, requestStatus: 'pending' }),
      );
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

export const fetchRequestPendingAccountFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'request-pending-account/fetchRequestPendingAccountFilters',
  async (_, { dispatch }) => {
    try {
      const response = await penaltyManagementApi.fetchRequestAccountFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

const initialState: RequestPendingInitialProperties = {
  requestPending: [],
  limit: 0,
  page: 0,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

export const requestPendingAccountSlice = createSlice({
  name: 'request-pending-account',
  initialState,
  reducers: {
    /**
     * Update the pending account filters
     * @param {any} state - The current state of the request pending account slice
     * @param {any} action - The payload action containing the filter update properties
     */
    updateRequestPendingAccountFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current state of the request pending account slice
     */
    disableRequestPendingAccountFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Function to change sortBy
     * @param {any} state - The current state of the request pending account slice
     * @param {any} action - The payload action containing the sort field and sort order
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Function to handle async actions
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder: ActionReducerMapBuilder<RequestPendingInitialProperties>) => {
    builder
      .addCase(fetchRequestPendingAccount.pending, (state: RequestPendingInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchRequestPendingAccount.fulfilled,
        (
          state: RequestPendingInitialProperties,
          action: PayloadAction<RequestPendingApiResponseInterface>,
        ) => {
          state.loading = false;
          state.requestPending = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchRequestPendingAccount.rejected, (state: RequestPendingInitialProperties) => {
        state.loading = false;
      });
    builder
      .addCase(
        fetchRequestPendingAccountFilters.pending,
        (state: RequestPendingInitialProperties) => {
          state.filterListLoading = true;
        },
      )
      .addCase(
        fetchRequestPendingAccountFilters.fulfilled,
        (
          state: RequestPendingInitialProperties,
          action: PayloadAction<CommonFilterProperties[]>,
        ) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...(action.payload || [])];
        },
      )
      .addCase(
        fetchRequestPendingAccountFilters.rejected,
        (state: RequestPendingInitialProperties) => {
          state.filterListLoading = false;
        },
      );
  },
});
export const {
  updateRequestPendingAccountFilters,
  disableRequestPendingAccountFilterApplied,
  setSortData,
} = requestPendingAccountSlice.actions;
export default requestPendingAccountSlice.reducer;
